import React, { useState } from 'react'

import clsx from 'clsx'

import BuildConfig from '$extensionSrc/buildConfig'
import ActionButton, {
  ActionButtonIcon,
  ActionButtonSize,
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import ImpressionLogger from '$extensionSrc/components/ImpressionLogger'
import LogContextProvider from '$extensionSrc/components/LogContextProvider'
import { internationalWaitlistTallyFormUrl } from '$extensionSrc/consts/urlConsts'

import styles from './InternationalWaitlistBanner.module.scss'

/**
 * From https://dev.to/jorik/country-code-to-flag-emoji-a21
 */
function getFlagEmoji(isoCountryCode): string {
  const codePoints = isoCountryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt())
  return String.fromCodePoint(...codePoints)
}

function getCountryName(isoCountryCode: string): string {
  if (isoCountryCode === 'GB') {
    return 'the United Kingdom'
  }

  const regionNamesInEnglish = new Intl.DisplayNames(['en'], { type: 'region' })
  return regionNamesInEnglish.of(isoCountryCode)
}

export default function InternationalWaitlistBanner({
  isoCountryCode,
}: {
  /**
   *  ISO 3166 two character country code
   * (from Cloudflare's CF-IPCountry header)
   */
  isoCountryCode: string
}) {
  const [isDismissed, setIsDismissed] = useState(false)

  if (isDismissed) {
    return null
  }

  return (
    <LogContextProvider
      component="InternationalWaitlistBanner"
      isoCountryCode={isoCountryCode}
    >
      <ImpressionLogger eventName="component:render" eventProps={{}}>
        <div
          className={clsx(styles.InternationalWaitlistBanner, 'high-shadow')}
        >
          <ActionButton
            ariaLabel="Close"
            className={styles.closeIcon}
            type={ActionButtonType.TERTIARY}
            size={ActionButtonSize.SMALL}
            iconName={ActionButtonIcon.CANCEL}
            iconOnly
            onClick={() => {
              setIsDismissed(true)
            }}
          />
          <div className={styles.content}>
            <div className={styles.title}>
              It looks like you're in {getCountryName(isoCountryCode)}{' '}
              {getFlagEmoji(isoCountryCode)}
            </div>
            <div className={styles.description}>
              Right now, the {BuildConfig.TENANT_NAME} extension is only
              available in the USA. Join the waitlist to know when it's
              available in your country.
            </div>
          </div>
          <ActionButton
            ariaLabel="Join the waitlist"
            className={styles.cta}
            type={ActionButtonType.PRIMARY}
            size={ActionButtonSize.MEDIUM}
            onClick={() => {
              window.open(internationalWaitlistTallyFormUrl, '_blank')
            }}
          >
            Join the waitlist
          </ActionButton>
        </div>
      </ImpressionLogger>
    </LogContextProvider>
  )
}
